import { useCallback, useEffect, useRef, useState, Suspense } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import classNames from 'classnames';
import Header from '../../global/Header';
import Footer from '../../global/Footer';
import {
  isTrackedPath,
  isTrashieContainerPath,
  isFooterPath,
  getRewardsV2RedirectPath,
  isFooterExcludedPath,
} from '../../utils/routes';

import { EVENT_STREAM_APP_TYPES, EVENT_STREAM_EVENT_TYPES } from '../../config/service';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import Loading from '../../global/Loading';
import { getConfigurations, sendEventStream } from '../../utils/service';

import InformativeBanner from '../../global/InformativeBanner/InformativeBanner';
import { isMobile } from '../../../../hooks/useBreakPoint';
import { TRASHIE_BASE_URL, isProd } from '../../../../config/config';
import { useAlert } from '../../../providers/AlertProvider';
import { signOut } from '../../../../utils/auth';
import {
  ROUTE_DASHBOARD_REWARDS,
  ROUTE_LOGIN,
  ROUTE_LOGOUT,
  ROUTE_MY_ACCOUNT,
  ROUTE_ACCOUNT_GIFT_CARDS,
  ROUTE_ACCOUNT_GIFT_CARDS_HISTORY,
  ROUTE_ACCOUNT_GIFT_CARDS_ALL,
  ROUTE_ACCOUNT_SETTINGS,
  ROUTE_ACCOUNT_SETTINGS_DETAILS,
  ROUTE_ACCOUNT_SETTINGS_LOGIN,
  ROUTE_ACCOUNT_SETTINGS_PASSWORD,
  ROUTE_TRANSACTIONS,
  ROUTE_TAKE_BACK_BAGS,
  ROUTE_TAKE_BACK_BAGS_DETAILS,
  ROUTE_TAKE_BACK_BAGS_BATCH,
  ROUTE_UNLIMITED,
  ROUTE_TRASHIE_UNLIMITED,
  ROUTE_REGISTER,
} from '../../config/routes';
import useHasAccessToGiftCards from '../../../../hooks/useHasAccessToGiftCards';
import {
  VIEW_HUB,
  VIEW_ACCOUNT_DETAILS,
  VIEW_LOGIN_SECURITY,
  VIEW_CHANGE_PASSWORD,
} from '../../views/AccountSettings/constants';
import {
  SHIPPING_LABELS_VIEW_HOME,
  SHIPPING_LABELS_VIEW_DETAIL,
  SHIPPING_LABELS_VIEW_BATCH,
  SHIPPING_LABELS_VIEW_INSTANT,
  SHIPPING_LABELS_VIEW_REGULAR,
  SHIPPING_LABELS_VIEW_PARAM,
} from '../../views/AccountTakeBackBags/constants';
import { setDocumentTitle } from '../../utils/utils';
import useGetSystemMessages from '../../utils/hooks/useGetSystemMessages';
import useRewardsV2Flag from '../../utils/hooks/useRewardsV2Flag';
import ACCOUNT_DRAWER_OPEN_EVENT, {
  ACCOUNT_DRAWER_CLOSE_EVENT,
  ACCOUNT_DRAWER_STATE_EVENT,
  ACCOUNT_DRAWER_SHOW_GIFT_CARDS_EVENT,
  ACCOUNT_DRAWER_SHOW_ACCOUNT_SETTINGS_EVENT,
  ACCOUNT_DRAWER_BACK_TO_ACCOUNT_EVENT,
  ACCOUNT_DRAWER_SHOW_TRANSACTIONS_EVENT,
  ACCOUNT_DRAWER_SHOW_MEMBERSHIP_ADDRESSES_EVENT,
  ACCOUNT_DRAWER_SHOW_TBB_EVENT,
  TRACKING_DRAWER_OPEN_EVENT,
  TRACKING_DRAWER_STATE_EVENT,
} from '../../config/events';
import { pushAccountDrawerViewedEvent } from '../../utils/gtm';
import lazyWithRetry from '../../../../utils/lazyWithRetry';

import { RenewalStatusProvider } from '../../global/RenewalMessaging';

import Style from './TrashieContainer.module.scss';

// Account/tracking drawer panels are lazy-loaded — each becomes its own chunk,
// fetched only when the user opens that drawer (see Suspense boundaries below).
// webpackPrefetch: the panels' chunk group includes the large shared vendor
// chunk; prefetching at browser-idle priority means opening a drawer no longer
// blocks on that download (it Suspense-gates the panel otherwise).
const AccountGiftCardsPanel = lazyWithRetry(() => import(/* webpackPrefetch: true */ '../../views/AccountGiftCards').then((m) => ({ default: m.AccountGiftCardsPanel })));
const AccountSettingsPanel = lazyWithRetry(() => import(/* webpackPrefetch: true */ '../../views/AccountSettings').then((m) => ({ default: m.AccountSettingsPanel })));
const AccountTransactionsPanel = lazyWithRetry(() => import(/* webpackPrefetch: true */ '../../views/AccountTransactions').then((m) => ({ default: m.AccountTransactionsPanel })));
const MembershipAddressesPanel = lazyWithRetry(() => import(/* webpackPrefetch: true */ '../../views/MembershipAddresses').then((m) => ({ default: m.MembershipAddressesPanel })));
const AccountTakeBackBagsPanel = lazyWithRetry(() => import(/* webpackPrefetch: true */ '../../views/AccountTakeBackBags'));
const TrashieMyAccount = lazyWithRetry(() => import(/* webpackPrefetch: true */ '../../views/MyAccount'));
const TrashieTrackingDetails = lazyWithRetry(() => import(/* webpackPrefetch: true */ '../../views/TrackingDetails').then((m) => ({ default: m.TrackingDetails })));

const {
  main,
  content,
  hasMobileFooter,
  loader,
} = Style;

const isAccountGiftCardsPath = (pathname) => (
  pathname === ROUTE_ACCOUNT_GIFT_CARDS
  || pathname.startsWith(`${ROUTE_ACCOUNT_GIFT_CARDS}/`)
);

const isAccountSettingsPath = (pathname) => (
  pathname === ROUTE_ACCOUNT_SETTINGS
  || pathname.startsWith(`${ROUTE_ACCOUNT_SETTINGS}/`)
);

const parseAccountSettingsPath = (pathname) => {
  if (pathname === ROUTE_ACCOUNT_SETTINGS_DETAILS) {
    return { initialView: VIEW_ACCOUNT_DETAILS };
  }
  if (pathname === ROUTE_ACCOUNT_SETTINGS_LOGIN) {
    return { initialView: VIEW_LOGIN_SECURITY };
  }
  if (pathname === ROUTE_ACCOUNT_SETTINGS_PASSWORD) {
    return { initialView: VIEW_CHANGE_PASSWORD };
  }
  return { initialView: VIEW_HUB };
};

const isTransactionsPath = (pathname) => pathname === ROUTE_TRANSACTIONS;

const parseGiftCardsPath = (pathname) => {
  if (pathname === ROUTE_ACCOUNT_GIFT_CARDS_HISTORY) {
    return { initialView: 'history', initialDetailOrderId: null };
  }
  if (pathname === ROUTE_ACCOUNT_GIFT_CARDS_ALL) {
    return { initialView: 'all', initialDetailOrderId: null };
  }
  if (pathname === ROUTE_ACCOUNT_GIFT_CARDS) {
    return { initialView: 'main', initialDetailOrderId: null };
  }
  if (pathname.startsWith(`${ROUTE_ACCOUNT_GIFT_CARDS}/`)) {
    const suffix = pathname.slice(`${ROUTE_ACCOUNT_GIFT_CARDS}/`.length);
    if (suffix && !suffix.includes('/')) {
      return { initialView: 'main', initialDetailOrderId: suffix };
    }
  }
  return { initialView: 'main', initialDetailOrderId: null };
};

const parseTbbPath = (pathname, search) => {
  const params = new URLSearchParams(search);
  if (pathname === ROUTE_TAKE_BACK_BAGS_BATCH) {
    return { initialView: SHIPPING_LABELS_VIEW_BATCH, code: null, batchId: params.get('batchId') || null };
  }
  if (pathname === ROUTE_TAKE_BACK_BAGS_DETAILS) {
    return { initialView: SHIPPING_LABELS_VIEW_DETAIL, code: params.get('code') || null, batchId: null };
  }
  // `?view=instant|regular` deep-links straight to a sub-screen (e.g. the order
  // confirmation's "Get My Shipping Labels" → Instant carousel).
  const view = params.get(SHIPPING_LABELS_VIEW_PARAM);
  if (view === SHIPPING_LABELS_VIEW_INSTANT || view === SHIPPING_LABELS_VIEW_REGULAR) {
    return { initialView: view, code: null, batchId: null };
  }
  return { initialView: SHIPPING_LABELS_VIEW_HOME, code: null, batchId: null };
};

const TBB_ACCOUNT_PATHS = [
  ROUTE_TAKE_BACK_BAGS,
  ROUTE_TAKE_BACK_BAGS_DETAILS,
  ROUTE_TAKE_BACK_BAGS_BATCH,
];
const isTbbAccountPath = (pathname) => TBB_ACCOUNT_PATHS.includes(pathname);

// Pages that redirect INTO a TBB account path on their own must never be
// stored as the "last non-account location": the TBB account-drawer bridge
// below would replace back onto them, re-triggering their redirect in an
// infinite loop (Max update depth / Safari's replaceState rate limit).
// - /unlimited & /trashie-unlimited: Subscription.jsx redirects subscribed
//   users to /take-back-bags.
// - /register: Register.jsx pushes already-CLAIMED codes to
//   /take-back-bags/details on every mount (LogRocket prod freeze, 2026-07-08).
const isSelfRedirectingPath = (pathname) => (
  pathname === ROUTE_UNLIMITED
  || pathname === ROUTE_TRASHIE_UNLIMITED
  || pathname === ROUTE_REGISTER
  || pathname.startsWith(`${ROUTE_REGISTER}/`)
);

const TrashieContainer = ({ children }) => {
  const { pathname, search, hash } = useLocation();
  const history = useHistory();
  const { isLoading, isSignedIn, user, userData } = useFirebaseAuth();
  const rewardsV2Enabled = useRewardsV2Flag();
  const {
    isLoading: isLoadingGiftCardsAccess,
    hasAccessToGiftCards,
  } = useHasAccessToGiftCards();
  const setAlert = useAlert();
  const isMobileSize = isMobile();

  const [isReady, setIsReady] = useState(false);
  const [isAccountDrawerMounted, setIsAccountDrawerMounted] = useState(false);
  const [isAccountDrawerVisible, setIsAccountDrawerVisible] = useState(false);
  const [isTrackingDrawerMounted, setIsTrackingDrawerMounted] = useState(false);
  const [isTrackingDrawerVisible, setIsTrackingDrawerVisible] = useState(false);
  const [giftCardsEntry, setGiftCardsEntry] = useState(null);
  const [accountSettingsEntry, setAccountSettingsEntry] = useState(null);
  const [transactionsEntry, setTransactionsEntry] = useState(null);
  const [membershipAddressesEntry, setMembershipAddressesEntry] = useState(null);
  const [tbbEntry, setTbbEntry] = useState(null);
  const giftCardsSheetCloseRef = useRef(null);
  const lastNonAccountLocationRef = useRef({
    pathname: ROUTE_DASHBOARD_REWARDS,
    search: '',
    hash: '',
  });
  const previousLocationRef = useRef({
    pathname,
    search,
    hash,
  });
  const skipNextRouteCloseRef = useRef(false);
  const closeAccountDrawerTimeoutRef = useRef(null);
  const closeTrackingDrawerTimeoutRef = useRef(null);

  const systemMessage = useGetSystemMessages();

  // For signed-out users on a public TrashieContainer path (e.g. /articles*), don't
  // block rendering on the PostHog rewardsV2 flag — it never resolves without auth.
  const isWaitingForRewardsFlag = typeof rewardsV2Enabled === 'undefined' && (isLoading || isSignedIn);
  const isNotTrashieContainer = !isTrashieContainerPath(pathname) || isWaitingForRewardsFlag;

  const handleEventStream = useCallback(async () => {
    await sendEventStream({
      event: EVENT_STREAM_EVENT_TYPES.USER_LAST_VISIT,
      application: EVENT_STREAM_APP_TYPES.TRASHIE,
      data: {
        url: window.location.href,
      },
    });
  }, []);

  const validateNonProductionEnvWhitelistedUsers = useCallback(async () => {
    if (isProd()) {
      setIsReady(true);
      return;
    }

    if (window.location.hostname === 'localhost') {
      setIsReady(true);
      return;
    }

    const { value, error } = await getConfigurations('nonProductionEnvWhitelistedUsers');

    if (error) {
      setAlert({
        type: 'alert',
        message: error,
        error,
      });
      return;
    }

    if (!(value.emails || []).includes(user?.email) &&
      !(value.domains || []).some(domain => (user?.email || '').includes(domain))
    ) {
      await signOut();
      window.location.href = TRASHIE_BASE_URL.production;
      return;
    }

    setIsReady(true);
  }, [user]);

  useEffect(() => {
    if (isTrackedPath(pathname) && isSignedIn && userData) {
      handleEventStream(userData);
    }
  }, [isSignedIn, userData, pathname]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (isSignedIn) {
      validateNonProductionEnvWhitelistedUsers();
    } else {
      setIsReady(true);
    }
  }, [isLoading, isSignedIn]);

  useEffect(() => {
    setDocumentTitle();
  }, [pathname]);

  const openAccountDrawer = useCallback(() => {
    if (closeAccountDrawerTimeoutRef.current) {
      clearTimeout(closeAccountDrawerTimeoutRef.current);
      closeAccountDrawerTimeoutRef.current = null;
    }
    if (closeTrackingDrawerTimeoutRef.current) {
      clearTimeout(closeTrackingDrawerTimeoutRef.current);
      closeTrackingDrawerTimeoutRef.current = null;
    }
    setIsTrackingDrawerVisible(false);
    setIsTrackingDrawerMounted(false);

    setIsAccountDrawerMounted(true);
    requestAnimationFrame(() => {
      setIsAccountDrawerVisible(true);
    });
  }, []);

  const closeAccountDrawer = useCallback(() => {
    if (closeAccountDrawerTimeoutRef.current) {
      clearTimeout(closeAccountDrawerTimeoutRef.current);
      closeAccountDrawerTimeoutRef.current = null;
    }

    setIsAccountDrawerVisible(false);
    const closeDelay = window.matchMedia('(max-width: 767px)').matches ? 0 : 260;
    closeAccountDrawerTimeoutRef.current = setTimeout(() => {
      setIsAccountDrawerMounted(false);
      setGiftCardsEntry(null);
      setAccountSettingsEntry(null);
      setTransactionsEntry(null);
      setMembershipAddressesEntry(null);
      setTbbEntry(null);
      giftCardsSheetCloseRef.current = null;
      closeAccountDrawerTimeoutRef.current = null;
    }, closeDelay);
  }, []);

  const openTrackingDrawer = useCallback(() => {
    if (closeTrackingDrawerTimeoutRef.current) {
      clearTimeout(closeTrackingDrawerTimeoutRef.current);
      closeTrackingDrawerTimeoutRef.current = null;
    }
    if (closeAccountDrawerTimeoutRef.current) {
      clearTimeout(closeAccountDrawerTimeoutRef.current);
      closeAccountDrawerTimeoutRef.current = null;
    }
    setIsAccountDrawerVisible(false);
    setIsAccountDrawerMounted(false);

    setIsTrackingDrawerMounted(true);
    requestAnimationFrame(() => {
      setIsTrackingDrawerVisible(true);
    });
  }, []);

  const handleDrawerBackdropClick = useCallback(() => {
    if (giftCardsSheetCloseRef.current) {
      giftCardsSheetCloseRef.current();
      return;
    }
    closeAccountDrawer();
  }, [closeAccountDrawer, history]);

  const closeTrackingDrawer = useCallback(() => {
    if (closeTrackingDrawerTimeoutRef.current) {
      clearTimeout(closeTrackingDrawerTimeoutRef.current);
      closeTrackingDrawerTimeoutRef.current = null;
    }

    setIsTrackingDrawerVisible(false);
    const closeDelay = window.matchMedia('(max-width: 767px)').matches ? 0 : 260;
    closeTrackingDrawerTimeoutRef.current = setTimeout(() => {
      setIsTrackingDrawerMounted(false);
      closeTrackingDrawerTimeoutRef.current = null;
    }, closeDelay);
  }, []);

  useEffect(() => {
    const previousLocation = previousLocationRef.current;
    const didLocationChange = previousLocation.pathname !== pathname
      || previousLocation.search !== search
      || previousLocation.hash !== hash;

    if (didLocationChange) {
      if (
        pathname !== ROUTE_MY_ACCOUNT
        && !isAccountGiftCardsPath(pathname)
        && !isAccountSettingsPath(pathname)
        && !isTransactionsPath(pathname)
        && !isTbbAccountPath(pathname)
        && !isSelfRedirectingPath(pathname)
      ) {
        lastNonAccountLocationRef.current = { pathname, search, hash };
      }

      if (isTrackingDrawerVisible) {
        closeTrackingDrawer();
      }

      if (
        pathname !== ROUTE_MY_ACCOUNT
        && !isAccountGiftCardsPath(pathname)
        && !isAccountSettingsPath(pathname)
        && !isTransactionsPath(pathname)
        && !isTbbAccountPath(pathname)
        && isAccountDrawerVisible
      ) {
        if (skipNextRouteCloseRef.current) {
          skipNextRouteCloseRef.current = false;
        } else {
          closeAccountDrawer();
        }
      }
    }

    previousLocationRef.current = { pathname, search, hash };
  }, [
    pathname,
    search,
    hash,
    isAccountDrawerVisible,
    isTrackingDrawerVisible,
    closeAccountDrawer,
    closeTrackingDrawer,
  ]);

  useEffect(() => {
    // Every drawer bridge below is a private route. Signed-out (or still
    // hydrating), leave the URL alone so PrivateRoute redirects to login with
    // it as the deep link. Opening the drawer here would replace the URL away
    // and leave the panel waiting on a session that never comes — e.g. the
    // labels email link spun forever on the Regular Labels loader.
    if (isLoading || !isSignedIn) {
      return;
    }

    if (pathname === ROUTE_MY_ACCOUNT) {
      const fallbackLocation = lastNonAccountLocationRef.current;
      const destinationPath = fallbackLocation.pathname || ROUTE_DASHBOARD_REWARDS;

      setGiftCardsEntry(null);
      setAccountSettingsEntry(null);
      setTransactionsEntry(null);
      setMembershipAddressesEntry(null);
      setTbbEntry(null);
      openAccountDrawer();
      skipNextRouteCloseRef.current = true;
      history.replace({
        pathname: destinationPath,
        search: fallbackLocation.search || '',
        hash: fallbackLocation.hash || '',
      });
      return;
    }

    if (isAccountSettingsPath(pathname)) {
      const fallbackLocation = lastNonAccountLocationRef.current;
      const destinationPath = fallbackLocation.pathname || ROUTE_DASHBOARD_REWARDS;
      setGiftCardsEntry(null);
      setTransactionsEntry(null);
      setMembershipAddressesEntry(null);
      setTbbEntry(null);
      setAccountSettingsEntry(parseAccountSettingsPath(pathname));
      openAccountDrawer();
      skipNextRouteCloseRef.current = true;
      history.replace({
        pathname: destinationPath,
        search: fallbackLocation.search || '',
        hash: fallbackLocation.hash || '',
      });
      return;
    }

    if (isAccountGiftCardsPath(pathname)) {
      // Wait for access resolution so denied users don't see the drawer flash.
      if (isLoadingGiftCardsAccess) {
        return;
      }
      const fallbackLocation = lastNonAccountLocationRef.current;
      const destinationPath = fallbackLocation.pathname || ROUTE_DASHBOARD_REWARDS;

      if (hasAccessToGiftCards) {
        setAccountSettingsEntry(null);
        setTransactionsEntry(null);
        setMembershipAddressesEntry(null);
        setTbbEntry(null);
        setGiftCardsEntry(parseGiftCardsPath(pathname));
        openAccountDrawer();
      } else {
        // eslint-disable-next-line no-console
        console.warn('[TrashieContainer] gift-cards deep-link denied', { hasAccessToGiftCards });
      }
      skipNextRouteCloseRef.current = true;
      history.replace({
        pathname: destinationPath,
        search: fallbackLocation.search || '',
        hash: fallbackLocation.hash || '',
      });
      return;
    }

    if (isTransactionsPath(pathname)) {
      const fallbackLocation = lastNonAccountLocationRef.current;
      const destinationPath = fallbackLocation.pathname || ROUTE_DASHBOARD_REWARDS;
      setGiftCardsEntry(null);
      setAccountSettingsEntry(null);
      setTransactionsEntry({});
      setMembershipAddressesEntry(null);
      setTbbEntry(null);
      openAccountDrawer();
      skipNextRouteCloseRef.current = true;
      history.replace({
        pathname: destinationPath,
        search: fallbackLocation.search || '',
        hash: fallbackLocation.hash || '',
      });
      return;
    }

    if (isTbbAccountPath(pathname)) {
      const fallbackLocation = lastNonAccountLocationRef.current;
      const destinationPath = fallbackLocation.pathname || ROUTE_DASHBOARD_REWARDS;
      setGiftCardsEntry(null);
      setAccountSettingsEntry(null);
      setTransactionsEntry(null);
      setMembershipAddressesEntry(null);
      setTbbEntry(parseTbbPath(pathname, search));
      openAccountDrawer();
      skipNextRouteCloseRef.current = true;
      history.replace({
        pathname: destinationPath,
        search: fallbackLocation.search || '',
        hash: fallbackLocation.hash || '',
      });
    }
  }, [
    pathname,
    search,
    history,
    openAccountDrawer,
    isLoading,
    isSignedIn,
    isLoadingGiftCardsAccess,
    hasAccessToGiftCards,
  ]);

  useEffect(() => {
    if (!giftCardsEntry) {
      return;
    }
    if (isLoadingGiftCardsAccess) {
      return;
    }
    if (!hasAccessToGiftCards) {
      setGiftCardsEntry(null);
    }
  }, [giftCardsEntry, isLoadingGiftCardsAccess, hasAccessToGiftCards]);

  useEffect(() => {
    const onOpenDrawer = () => {
      openAccountDrawer();
    };

    window.addEventListener(ACCOUNT_DRAWER_OPEN_EVENT, onOpenDrawer);
    return () => {
      window.removeEventListener(ACCOUNT_DRAWER_OPEN_EVENT, onOpenDrawer);
    };
  }, [openAccountDrawer]);

  useEffect(() => {
    const onCloseDrawer = () => {
      closeAccountDrawer();
    };

    window.addEventListener(ACCOUNT_DRAWER_CLOSE_EVENT, onCloseDrawer);
    return () => {
      window.removeEventListener(ACCOUNT_DRAWER_CLOSE_EVENT, onCloseDrawer);
    };
  }, [closeAccountDrawer]);

  useEffect(() => {
    const onShowGiftCards = (event) => {
      if (!hasAccessToGiftCards) return;
      setAccountSettingsEntry(null);
      setTransactionsEntry(null);
      setMembershipAddressesEntry(null);
      setTbbEntry(null);
      setGiftCardsEntry({
        initialView: 'main',
        initialDetailOrderId: event?.detail?.initialDetailOrderId || null,
      });
      openAccountDrawer();
    };
    window.addEventListener(ACCOUNT_DRAWER_SHOW_GIFT_CARDS_EVENT, onShowGiftCards);
    return () => {
      window.removeEventListener(ACCOUNT_DRAWER_SHOW_GIFT_CARDS_EVENT, onShowGiftCards);
    };
  }, [openAccountDrawer, hasAccessToGiftCards]);

  useEffect(() => {
    const onShowAccountSettings = () => {
      setGiftCardsEntry(null);
      setTransactionsEntry(null);
      setMembershipAddressesEntry(null);
      setTbbEntry(null);
      setAccountSettingsEntry({ initialView: VIEW_HUB });
      openAccountDrawer();
    };
    window.addEventListener(ACCOUNT_DRAWER_SHOW_ACCOUNT_SETTINGS_EVENT, onShowAccountSettings);
    return () => {
      window.removeEventListener(
        ACCOUNT_DRAWER_SHOW_ACCOUNT_SETTINGS_EVENT,
        onShowAccountSettings,
      );
    };
  }, [openAccountDrawer]);

  useEffect(() => {
    const onShowTransactions = () => {
      setGiftCardsEntry(null);
      setAccountSettingsEntry(null);
      setTransactionsEntry({});
      setMembershipAddressesEntry(null);
      setTbbEntry(null);
      openAccountDrawer();
    };
    window.addEventListener(ACCOUNT_DRAWER_SHOW_TRANSACTIONS_EVENT, onShowTransactions);
    return () => {
      window.removeEventListener(ACCOUNT_DRAWER_SHOW_TRANSACTIONS_EVENT, onShowTransactions);
    };
  }, [openAccountDrawer]);

  useEffect(() => {
    const onShowMembershipAddresses = () => {
      setGiftCardsEntry(null);
      setAccountSettingsEntry(null);
      setTransactionsEntry(null);
      setTbbEntry(null);
      setMembershipAddressesEntry({});
      openAccountDrawer();
    };
    window.addEventListener(
      ACCOUNT_DRAWER_SHOW_MEMBERSHIP_ADDRESSES_EVENT,
      onShowMembershipAddresses,
    );
    return () => {
      window.removeEventListener(
        ACCOUNT_DRAWER_SHOW_MEMBERSHIP_ADDRESSES_EVENT,
        onShowMembershipAddresses,
      );
    };
  }, [openAccountDrawer]);

  useEffect(() => {
    const onShowTbb = (event) => {
      setGiftCardsEntry(null);
      setAccountSettingsEntry(null);
      setTransactionsEntry(null);
      setMembershipAddressesEntry(null);
      setTbbEntry(event?.detail ?? {
        initialView: SHIPPING_LABELS_VIEW_HOME, code: null, batchId: null,
      });
      openAccountDrawer();
    };
    window.addEventListener(ACCOUNT_DRAWER_SHOW_TBB_EVENT, onShowTbb);
    return () => window.removeEventListener(ACCOUNT_DRAWER_SHOW_TBB_EVENT, onShowTbb);
  }, [openAccountDrawer]);

  useEffect(() => {
    const onBackToAccount = () => {
      setGiftCardsEntry(null);
      setAccountSettingsEntry(null);
      setTransactionsEntry(null);
      setMembershipAddressesEntry(null);
      setTbbEntry(null);
      giftCardsSheetCloseRef.current = null;
    };
    window.addEventListener(ACCOUNT_DRAWER_BACK_TO_ACCOUNT_EVENT, onBackToAccount);
    return () => {
      window.removeEventListener(ACCOUNT_DRAWER_BACK_TO_ACCOUNT_EVENT, onBackToAccount);
    };
  }, []);

  useEffect(() => {
    const onOpenDrawer = () => {
      openTrackingDrawer();
    };

    window.addEventListener(TRACKING_DRAWER_OPEN_EVENT, onOpenDrawer);
    return () => {
      window.removeEventListener(TRACKING_DRAWER_OPEN_EVENT, onOpenDrawer);
    };
  }, [openTrackingDrawer]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent(ACCOUNT_DRAWER_STATE_EVENT, {
      detail: { isOpen: isAccountDrawerVisible },
    }));
  }, [isAccountDrawerVisible]);

  useEffect(() => {
    if (!isAccountDrawerVisible) {
      return;
    }
    pushAccountDrawerViewedEvent();
  }, [isAccountDrawerVisible]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent(TRACKING_DRAWER_STATE_EVENT, {
      detail: { isOpen: isTrackingDrawerVisible },
    }));
  }, [isTrackingDrawerVisible]);

  useEffect(() => {
    if (!isNotTrashieContainer && isSignedIn && user && !user.emailVerified) {
      history.replace(ROUTE_LOGIN);
    }
  }, [isNotTrashieContainer, isLoading, isSignedIn, user]);

  useEffect(() => {
    if (!isNotTrashieContainer && isSignedIn && userData?.forcePwdReset) {
      history.replace(ROUTE_LOGOUT);
    }
  }, [isNotTrashieContainer, isSignedIn, userData]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    if (isAccountDrawerMounted || isTrackingDrawerMounted) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isAccountDrawerMounted, isTrackingDrawerMounted]);

  useEffect(() => () => {
    if (closeAccountDrawerTimeoutRef.current) {
      clearTimeout(closeAccountDrawerTimeoutRef.current);
    }
    if (closeTrackingDrawerTimeoutRef.current) {
      clearTimeout(closeTrackingDrawerTimeoutRef.current);
    }
  }, []);

  if (rewardsV2Enabled === true) {
    const rewardsV2RedirectPath = getRewardsV2RedirectPath({ pathname, search, hash });

    if (rewardsV2RedirectPath) {
      history.replace(rewardsV2RedirectPath);
      return null;
    }
  }

  if (isNotTrashieContainer) {
    return null;
  }

  // RenewalStatusProvider wraps the whole signed-in shell so the routed views,
  // the account drawer and its own entry sheet share one renewal-status read.
  return (
    <RenewalStatusProvider>
      <div className={main}>
        <Header isBannerVisible={!!systemMessage} />
        <div className={classNames(content, {
          [hasMobileFooter]: isMobileSize && isFooterPath(pathname),
          [Style.noTopOffset]: isMobileSize && pathname === ROUTE_MY_ACCOUNT,
        })}
        >
          {isLoading || !isReady ? (
            <div className={loader}>
              <Loading />
            </div>
          ) : (
            <>
              {systemMessage && (
              <InformativeBanner
                isNotification
                mainText={systemMessage}
              />
              )}
              {children}
            </>
          )}
        </div>
        {((isMobileSize &&
        !isFooterPath(pathname)) ||
        isFooterExcludedPath(pathname)) ? null : <Footer />}
        {isAccountDrawerMounted && (
        <div
          className={classNames(Style.accountDrawerLayer, {
            [Style.accountDrawerLayerVisible]: isAccountDrawerVisible,
          })}
          role="presentation"
        >
          <button
            type="button"
            aria-label="Close account drawer"
            className={Style.accountDrawerBackdrop}
            onClick={handleDrawerBackdropClick}
          />
          <div className={classNames(Style.accountDrawerPanel, {
            [Style.accountDrawerPanelVisible]: isAccountDrawerVisible,
          })}
          >
            <Suspense fallback={<Loading />}>
              {giftCardsEntry && (
                <AccountGiftCardsPanel
                  initialView={giftCardsEntry.initialView}
                  initialDetailOrderId={giftCardsEntry.initialDetailOrderId}
                  sheetCloseRef={giftCardsSheetCloseRef}
                />
              )}
              {!giftCardsEntry && accountSettingsEntry && (
                <AccountSettingsPanel
                  initialView={accountSettingsEntry.initialView}
                />
              )}
              {!giftCardsEntry && !accountSettingsEntry && transactionsEntry && (
                <AccountTransactionsPanel />
              )}
              {!giftCardsEntry && !accountSettingsEntry && !transactionsEntry
                && membershipAddressesEntry && (
                <MembershipAddressesPanel />
              )}
              {!giftCardsEntry && !accountSettingsEntry && !transactionsEntry
                && !membershipAddressesEntry && tbbEntry && (
                <AccountTakeBackBagsPanel
                  initialView={tbbEntry.initialView}
                  initialCode={tbbEntry.code}
                  initialBatchId={tbbEntry.batchId}
                />
              )}
              {!giftCardsEntry && !accountSettingsEntry && !transactionsEntry
                && !membershipAddressesEntry && !tbbEntry && (
                <TrashieMyAccount isDrawer onClose={closeAccountDrawer} />
              )}
            </Suspense>
          </div>
        </div>
        )}
        {isTrackingDrawerMounted && (
        <div
          className={classNames(Style.trackingDrawerLayer, {
            [Style.trackingDrawerLayerVisible]: isTrackingDrawerVisible,
          })}
          role="presentation"
        >
          <button
            type="button"
            aria-label="Close tracking drawer"
            className={Style.accountDrawerBackdrop}
            onClick={closeTrackingDrawer}
          />
          <div className={classNames(Style.trackingDrawerPanel, {
            [Style.trackingDrawerPanelVisible]: isTrackingDrawerVisible,
          })}
          >
            <Suspense fallback={<Loading />}>
              <TrashieTrackingDetails isDrawer />
            </Suspense>
          </div>
        </div>
        )}
      </div>
    </RenewalStatusProvider>
  );
};

export default TrashieContainer;
